// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, FormControlLabel, Grid, InputLabel,
  MenuItem, Select, Switch, TextField, Typography,
} from '@mui/material';
import { TIMEZONES } from './commun';

/**
 * Champs d'un établissement, partagés par la création, la correction et
 * l'étape 2 de l'assistant d'ouverture, pour que ces formulaires ne divergent
 * pas (fuseau, longueur du code postal).
 */
export function SiteFields({ valeurs: formValues, onChange, disabled = false, avecExploitation: withOperations = true,
  comptesVigeos: vigeosAccounts = [], actif: active, onActif: onActive = null }) {
  const update = (patch) => onChange({ ...formValues, ...patch });

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth size="small" required label="Nom de l'établissement"
            value={formValues.name} disabled={disabled}
            onChange={(e) => update({ name: e.target.value })}
            helperText="Deux caractères minimum, et unique chez ce client"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth size="small" label="Adresse" value={formValues.address} disabled={disabled}
            onChange={(e) => update({ address: e.target.value })}
          />
        </Grid>
        <Grid item xs={7}>
          <TextField
            fullWidth size="small" label="Ville" value={formValues.city} disabled={disabled}
            onChange={(e) => update({ city: e.target.value })}
          />
        </Grid>
        <Grid item xs={5}>
          <TextField
            fullWidth size="small" label="Code postal" value={formValues.postal_code}
            inputProps={{ maxLength: 10 }} disabled={disabled}
            onChange={(e) => update({ postal_code: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth size="small" label="Pays" value={formValues.country} disabled={disabled}
            onChange={(e) => update({ country: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>Fuseau horaire</InputLabel>
            <Select
              value={TIMEZONES.includes(formValues.timezone) ? formValues.timezone : 'Europe/Paris'}
              label="Fuseau horaire"
              onChange={(e) => update({ timezone: e.target.value })}
            >
              {TIMEZONES.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {withOperations && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Exploitation Vigeos</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Identifiant du serveur d'inférence"
                value={formValues.server_id} inputProps={{ maxLength: 64 }} disabled={disabled}
                onChange={(e) => update({ server_id: e.target.value })}
                helperText="À renseigner à la pose, une fois le serveur du site enrôlé. Peut rester vide."
              />
            </Grid>
            {vigeosAccounts.length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small" disabled={disabled}>
                  <InputLabel>Compte de provisionnement</InputLabel>
                  <Select
                    value={formValues.owner_user_id}
                    label="Compte de provisionnement"
                    onChange={(e) => update({ owner_user_id: e.target.value })}
                  >
                    <MenuItem value="">Le compte qui enregistre (défaut)</MenuItem>
                    {vigeosAccounts.map((u) => (
                      <MenuItem key={u.id} value={u.id}>{u.full_name || u.email}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Compte Vigeos porté par les chambres du site. Sans lui, la console du
                  client ne peut créer aucune chambre.
                </Typography>
              </Grid>
            )}
            {onActive && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={active} onChange={(e) => onActive(e.target.checked)} />}
                  label={active ? 'Établissement en service' : 'Établissement désactivé'}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  La désactivation n&apos;efface rien et ne coupe aucune session : alertes,
                  journal et chambres restent lisibles. Elle interdit la création de
                  nouvelles chambres et de nouveaux résidents.
                </Typography>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </>
  );
}

/**
 * Rattacher un établissement à un client, ou corriger celui qui existe.
 *
 * `dialogue` porte tout l'état de la fenêtre (mode, valeurs initiales, valeurs
 * courantes, erreur, activité). Le parent le tient, parce que c'est lui qui
 * connaît le client et qui recharge sa fiche après l'enregistrement.
 */
export default function DialogueSite({ dialogue, onChange, onFermer: onClose, onEnregistrer: onSave, comptesVigeos: vigeosAccounts }) {
  if (!dialogue) return null;
  const creation = dialogue.mode === 'creation';

  return (
    <Dialog open onClose={() => !dialogue.enCours && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        {creation
          ? `Rattacher un établissement à ${dialogue.clientNom}`
          : "Modifier l'établissement"}
      </DialogTitle>
      <DialogContent dividers>
        {dialogue.erreur && <Alert severity="error" sx={{ mb: 2 }}>{dialogue.erreur}</Alert>}
        {creation && (
          <Alert severity="info" sx={{ mb: 2 }}>
            La direction du client verra ce nouvel établissement dès sa prochaine
            connexion, sans qu&apos;aucun compte soit à créer. Il naît vide : les
            chambres s&apos;importent depuis la fiche du client (onglet Chambres) ou
            depuis sa console.
          </Alert>
        )}
        <SiteFields
          valeurs={dialogue.valeurs}
          onChange={(formValues) => onChange({ valeurs: formValues })}
          comptesVigeos={vigeosAccounts}
          actif={dialogue.actif}
          onActif={creation ? null : (active) => onChange({ actif: active })}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={dialogue.enCours}>Annuler</Button>
        <Button
          variant="contained" onClick={onSave}
          disabled={dialogue.enCours || dialogue.valeurs.name.trim().length < 2}
        >
          {dialogue.enCours ? <CircularProgress size={18} /> : (creation ? 'Rattacher' : 'Enregistrer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
